import React from 'react';
import { Swords, Crown } from 'lucide-react';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * ChessGameEditor - Editor component for the chess game block
 * Allows configuring title, owner color, and theme matching
 */
const ChessGameEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const content = block.content || {};

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#749555]/20 rounded-lg">
            <Swords size={24} className="text-[#749555]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">Interactive Chess Game</h4>
            <p className="text-sm text-gray-400">
              Visitors can challenge you to a game! You play as {content.ownerColor || 'white'},
              everyone else plays as {content.ownerColor === 'black' ? 'white' : 'black'}.
            </p>
          </div>
        </div>
      </div>

      {/* Title Input */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">
          Section Title
        </label>
        <input
          type="text"
          value={content.title || 'Play Chess With Me'}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="Play Chess With Me"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#749555] transition-colors"
        />
      </div>

      {/* Color Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">
          Your Color
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => handleChange('ownerColor', 'white')}
            className={`flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${
              content.ownerColor === 'white' || !content.ownerColor
                ? 'border-[#749555] bg-[#749555]/10 text-white'
                : 'border-gray-700 bg-[#1a1a2e] text-gray-400 hover:border-gray-600'
            }`}
          >
            <div className="w-4 h-4 rounded-full bg-white border border-gray-300"></div>
            <span className="font-medium">White</span>
          </button>
          <button
            type="button"
            onClick={() => handleChange('ownerColor', 'black')}
            className={`flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${
              content.ownerColor === 'black'
                ? 'border-[#749555] bg-[#749555]/10 text-white'
                : 'border-gray-700 bg-[#1a1a2e] text-gray-400 hover:border-gray-600'
            }`}
          >
            <div className="w-4 h-4 rounded-full bg-gray-800 border border-gray-600"></div>
            <span className="font-medium">Black</span>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1.5">
          You'll play this color; visitors will play the opposite
        </p>
      </div>

      {/* Theme Match Toggle — entire row is clickable */}
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          handleChange('matchThemeSquares', !content.matchThemeSquares);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleChange('matchThemeSquares', !content.matchThemeSquares); } }}
        className="flex items-center justify-between p-3 bg-[#1a1a2e] border border-gray-700 rounded-lg cursor-pointer hover:border-gray-600 transition-colors"
      >
        <div>
          <span className="text-sm font-medium text-gray-300">
            Match Theme Color
          </span>
          <p className="text-xs text-gray-500 mt-0.5">
            Use your website's accent color for the dark squares
          </p>
        </div>
        <div
          className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
            content.matchThemeSquares === true
              ? 'bg-[#749555]'
              : 'bg-gray-600'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
              content.matchThemeSquares === true
                ? 'translate-x-5'
                : 'translate-x-0'
            }`}
          />
        </div>
      </div>

      {/* Preview Info */}
      <div className="text-sm text-gray-400 space-y-2">
        <p>This block displays:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-500">
          <li>Interactive chessboard</li>
          <li>Current game status & whose turn</li>
          <li>Move history in PGN format</li>
          <li>Real-time synchronization</li>
        </ul>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
      <SizeSpacingPanel style={content.style || {}} onChange={handleStyleChange} features={BLOCK_FEATURES['chess-game']} />
      <ColorsFontsPanel style={content.style || {}} onChange={handleStyleChange} features={BLOCK_FEATURES['chess-game']} theme={theme || {}} blockType="chess-game" />
      </>)}
    </div>
  );
};

export default ChessGameEditor;
